import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import ConfirmModal from "../components/ConfirmModal";
import { toast } from "sonner";
import {
  Loader2, Plus, X, Target, Rocket, Trash2, Users, CheckCircle2, HelpCircle, ChevronDown, ChevronUp,
} from "lucide-react";

// Minimum Usable ICP Vertical Slice (post-Step-1B) — the first frontend
// surface for the ICP module. Reuses Packages 1B.0-1B.6's backend
// unchanged: identity+version CRUD, the review lifecycle, the publish
// orchestrator, and the persona-link stack. This page follows the same
// list+modal-editor pattern already established by
// `RetailLinkProfilesAdmin.jsx` (plain Tailwind, no shadcn/ui, in-app
// toasts via sonner) for visual/structural consistency with the rest of
// the tenant dashboard.
//
// Product Owner workflow correction (2026-07-22): editing a published
// ICP must produce a COMPLETE editable continuation — content AND
// Buyer Persona/Catalog/Resource links — and the Library must clearly
// show both the live published version and any working draft at once,
// never conflate them into one ambiguous "latest" summary. See
// `docs` note in `services/icp/version_service.py::create_draft_from_version`
// for the backend half of this correction.
//
// Deliberate MVP scope choices (see the workstream's own scope-control
// rule — reuse what exists, expose only what's needed for practical use):
//   - Only ONE assistant↔ICP association surface exists here (bottom of
//     this page) rather than a new standalone Assistants admin page,
//     since none exists yet and building one is out of scope.
//   - List/array content fields (pain points, buying triggers, goals,
//     differentiators, key messages, discovery questions) are edited as
//     newline-separated text areas, not dynamic add/remove row widgets —
//     simpler, reliable, and explicitly permitted ("use text areas,
//     repeatable lists, or simple structured controls").
//   - Objections and Qualification Criteria are genuinely two/three-field
//     structures on the backend, so those two sections use small
//     repeatable rows (add/remove), not free text.
//   - No archive/restore, template, or AI-suggestion controls (all
//     explicitly out of scope for this release).

const TARGET_TYPES = [
  { value: "organization", label: "Organization / Business" },
  { value: "consumer", label: "Individual Consumer" },
  { value: "household", label: "Household" },
  { value: "professional", label: "Professional" },
  { value: "channel_partner", label: "Channel Partner" },
  { value: "government_entity", label: "Government Entity" },
  { value: "mixed", label: "Mixed" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch (_e) {
    return iso;
  }
}

function StatusPill({ status }) {
  const map = {
    draft: "bg-amber-50 text-amber-700 border-amber-200",
    published: "bg-emerald-50 text-emerald-700 border-emerald-200",
    superseded: "bg-slate-100 text-slate-500 border-slate-300",
  };
  return (
    <span className={"text-[10px] font-medium px-2 py-0.5 rounded-full border uppercase tracking-wider " + (map[status] || map.draft)}>
      {status || "draft"}
    </span>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

// Newline-separated text list ↔ string[] — the shared "simple structured
// control" for every plain string-array content field.
function StringListField({ label, hint, value, onChange, placeholder }) {
  const text = (value || []).join("\n");
  return (
    <Field label={label} hint={hint}>
      <textarea
        value={text}
        onChange={(e) => onChange(e.target.value.split("\n"))}
        rows={3}
        placeholder={placeholder}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
      />
    </Field>
  );
}

function cleanStrings(arr) {
  return (arr || []).map((s) => (s || "").trim()).filter(Boolean);
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function IcpLibraryAdminPage() {
  const [identities, setIdentities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null | {mode:'create'} | {mode:'edit', icpId, version, publishedVersion, draftVersion}

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/icp");
      setIdentities(r?.data?.identities || []);
    } catch (e) {
      toast.error("Could not load the ICP Library.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const publishedIcps = useMemo(
    () => identities.filter((i) => i.current_published_version != null),
    [identities]
  );

  const openIcp = (row, which) => {
    const pub = row.published_version;
    const draft = row.draft_version;
    // The primary Open/Continue-editing action always resolves to the
    // working draft whenever one exists (Product Owner correction) —
    // "which" lets a specific action (View live version) override that.
    const target = which === "published" ? pub : (draft || pub);
    if (!target) return;
    setEditor({
      mode: "edit",
      icpId: row.id,
      version: target.version,
      publishedVersion: pub?.version ?? null,
      draftVersion: draft?.version ?? null,
    });
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="icp-library-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-600" /> ICP Library
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Ideal Customer Profiles describe who your Sales Assistant should sound like it
              understands — target customer, pain points, value proposition, and Buyer Personas.
              Publish an ICP, then select it below for a Sales Assistant to use in real conversations.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditor({ mode: "create" })}
            data-testid="icp-new-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4" /> Create ICP
          </button>
        </div>

        <WorkflowHelpPanel />

        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
        ) : identities.length === 0 ? (
          <div
            className="bg-white border border-dashed border-slate-300 rounded-xl px-6 py-12 text-center text-sm text-slate-500"
            data-testid="icp-library-empty"
          >
            <Target className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            No ICPs yet. An ICP guides your Sales Brain and Sales Assistants on who they're
            talking to and how to sell to them.
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setEditor({ mode: "create" })}
                data-testid="icp-empty-create-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
              >
                <Plus className="w-4 h-4" /> Create ICP
              </button>
            </div>
          </div>
        ) : (
          <div className="grid gap-3" data-testid="icp-library-list">
            {identities.map((row) => {
              const pub = row.published_version;
              const draft = row.draft_version;
              const name = draft?.name || pub?.name || "Untitled ICP";
              const industry = draft?.industry || pub?.industry;
              return (
                <div
                  key={row.id}
                  data-testid={`icp-row-${row.id}`}
                  className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 hover:border-slate-300"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-base font-semibold text-slate-900">{name}</h2>
                        {industry && <span className="text-xs text-slate-500">{industry}</span>}
                      </div>

                      {/* Live version / working draft — always shown as two
                          distinct lines, never conflated (Product Owner
                          correction, 2026-07-22). */}
                      <div className="mt-1.5 space-y-0.5" data-testid={`icp-version-summary-${row.id}`}>
                        {pub && (
                          <div className="text-xs text-slate-700 flex items-center gap-1.5" data-testid={`icp-live-version-${row.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                            <span className="font-medium">Live version: v{pub.version}</span>
                            <StatusPill status="published" />
                          </div>
                        )}
                        {draft && (
                          <div className="text-xs text-slate-700 flex items-center gap-1.5" data-testid={`icp-draft-version-${row.id}`}>
                            <span className="w-3.5 h-3.5 inline-block rounded-full bg-amber-400" aria-hidden="true" />
                            <span className="font-medium">Working draft: v{draft.version}</span>
                            {!pub && <span className="text-slate-400">(not yet published)</span>}
                          </div>
                        )}
                      </div>

                      <div className="text-xs text-slate-500 mt-1.5">
                        Updated {fmtDate(draft?.updated_at || pub?.updated_at || row.updated_at)}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5">
                      {draft && (
                        <button
                          type="button"
                          onClick={() => openIcp(row, "draft")}
                          data-testid={`icp-continue-draft-${row.id}`}
                          className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800"
                        >
                          Continue editing draft
                        </button>
                      )}
                      {pub && (
                        <button
                          type="button"
                          onClick={() => openIcp(row, "published")}
                          data-testid={`icp-view-live-${row.id}`}
                          className="px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
                        >
                          View live version
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <AssistantIcpSection publishedIcps={publishedIcps} />
      </div>

      {editor && (
        <IcpEditor
          mode={editor.mode}
          icpId={editor.icpId}
          version={editor.version}
          publishedVersionNumber={editor.publishedVersion}
          draftVersionNumber={editor.draftVersion}
          onClose={() => setEditor(null)}
          onSaved={() => { refresh(); }}
        />
      )}
    </DashboardLayout>
  );
}

// ---------------------------------------------------------------------------
// Inline ICP Help (Product Owner correction, 2026-07-22; expanded into a
// full tenant-facing guide, 2026-07-22) — a concise, collapsible
// explainer using this codebase's established hint-text pattern
// (`Field`'s `hint` prop / ChatbotSettings' per-row `help` strings), not
// a new Help Center or tenant documentation system. Plain language only
// — no internal persistence/architecture terminology, and Section 11
// (Platform templates) is explicitly labeled a planned capability, not
// something available today.
// ---------------------------------------------------------------------------

const ICP_HELP_SECTIONS = [
  {
    title: "What is an ICP?",
    body: "An Ideal Customer Profile (ICP) describes the customers you want to sell to — their pain points, buying triggers, desired outcomes, the people involved in the purchase (Buyer Personas), your messaging, discovery questions, qualification guidance, and your recommended sales approach.",
  },
  {
    title: "Company Profile versus ICP",
    body: "Your Company Profile describes your own company and applies across your whole account. An ICP describes a target customer or audience instead. You can create as many ICPs as you need.",
  },
  {
    title: "Multiple ICPs",
    body: "Most tenants define more than one ICP for different audiences, for example: Healthcare Practices, Grocery Stores, Restaurants, Enterprise Customers, Internal Staff, or any other audience you sell to.",
  },
  {
    title: "Draft",
    body: "A draft is editable. Draft changes never affect live conversations until the draft is published. Use \"Save draft\" to keep your work without publishing yet. A working draft can exist alongside a live published version.",
  },
  {
    title: "Published and Live",
    body: "Published means read-only. The live published version is the version your Sales Assistant actually uses. Publishing a new draft replaces the previous live version — the previous version becomes historical and is not edited directly.",
  },
  {
    title: "Editing a published ICP",
    steps: [
      "Open the live version.",
      "Select \"Create new draft to edit,\" or continue your existing working draft if one is already open.",
      "Make your changes.",
      "Use \"Save draft\" to continue later.",
      "Use \"Save & Publish\" when you're ready to go live.",
      "The current live version stays active for your Sales Assistant until the new draft is published.",
    ],
  },
  {
    title: "Buyer Personas",
    body: "Buyer Personas represent the people involved in the purchase — owners, administrators, managers, decision-makers, influencers, and end users. Personas are linked to an ICP, and those links are carried into a new draft automatically.",
  },
  {
    title: "Catalog and Resources",
    body: "Catalog and Resource links provide supporting product and business knowledge for an ICP. They are carried into a new working draft, and the Sales Brain may use this approved linked information during conversations.",
  },
  {
    title: "Sales Assistant association",
    body: "Only published ICPs can be assigned to a Sales Assistant. The assistant stores a reference to the ICP — it does not own or copy it. Changes take effect for the assistant only after the new draft is published.",
  },
  {
    title: "Customer conversations",
    body: "The Sales Brain uses the currently selected published ICP. Draft changes are never used in live conversations — only what you publish. New conversations always use the latest published version. If no ICP is selected, your Sales Assistant continues to work exactly as it did before.",
  },
  {
    title: "Platform templates versus tenant ICPs",
    planned: true,
    body: "Planned capability — not available yet. In a future release, Rudras-AI will offer master ICP templates that remain platform-owned. You will be able to copy a master template into your own ICP Library, where your copy becomes fully yours to customize. The original master template is never changed.",
  },
  {
    title: "Recommended workflow",
    steps: [
      "Create an ICP.",
      "Enter target-customer information.",
      "Add Buyer Personas.",
      "Add commercial and sales context.",
      "Save the draft.",
      "Review it.",
      "Save and publish.",
      "Assign it to a Sales Assistant.",
      "Test a real conversation.",
      "Improve it based on actual usage.",
    ],
  },
];

function WorkflowHelpPanel() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl" data-testid="icp-workflow-help">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="icp-workflow-help-toggle"
        className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium text-slate-700"
      >
        <span className="inline-flex items-center gap-1.5">
          <HelpCircle className="w-4 h-4 text-slate-400" /> How ICP drafts and publishing work
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-0.5 space-y-3" data-testid="icp-workflow-help-content">
          {ICP_HELP_SECTIONS.map((s, i) => (
            <div key={s.title} data-testid={`icp-help-section-${i}`}>
              <h4 className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                {s.title}
                {s.planned && (
                  <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-700 uppercase tracking-wider">
                    Planned — not available yet
                  </span>
                )}
              </h4>
              {s.body && <p className="text-xs text-slate-600 mt-0.5">{s.body}</p>}
              {s.steps && (
                <ol className="text-xs text-slate-600 mt-0.5 list-decimal list-inside space-y-0.5">
                  {s.steps.map((step, j) => <li key={j}>{step}</li>)}
                </ol>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assistant ↔ ICP association (Phase 6) — mirrors the established
// AssistantLinkProfilesSection pattern from RetailLinkProfilesAdmin.jsx.
// Only PUBLISHED ICPs are ever offered — draft ICPs are structurally
// excluded from `publishedIcps` before this component ever sees them.
// ---------------------------------------------------------------------------

function AssistantIcpSection({ publishedIcps }) {
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selections, setSelections] = useState({}); // assistant_id -> icp_id (pending edit)
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/assistants").catch(() => ({ data: { assistants: [] } }));
      setAssistants(r?.data?.assistants || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const icpLabel = (icp) => {
    const name = icp?.published_version?.name || "Untitled ICP";
    return icp?.published_version?.industry ? `${name} (${icp.published_version.industry})` : name;
  };

  const currentSelection = (assistant) =>
    selections[assistant.id] !== undefined ? selections[assistant.id] : (assistant.icp_id || "");

  const save = async (assistant) => {
    const icpId = currentSelection(assistant);
    setSavingId(assistant.id);
    try {
      await api.patch(`/assistants/${assistant.id}`, { icp_id: icpId || null });
      toast.success(`"${assistant.assistant_name}" will now use ${icpId ? "the selected ICP" : "no ICP"} in conversations.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the ICP association.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="assistant-icp-section">
      <div>
        <div className="font-medium text-sm text-slate-900 flex items-center gap-1.5">
          <Users className="w-4 h-4 text-slate-400" /> Use an ICP with a Sales Assistant
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          Select one published ICP per Sales Assistant. The Assistant reads the ICP as context —
          it never owns or copies it, so updates to the ICP apply the next time it's used.
        </div>
      </div>

      {loading ? (
        <div className="py-6 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : assistants.length === 0 ? (
        <div className="text-xs text-slate-500" data-testid="assistant-icp-no-assistants">
          No Sales Assistant found yet. Create one under Resources &amp; AI Assistant.
        </div>
      ) : publishedIcps.length === 0 ? (
        <div className="text-xs text-slate-500" data-testid="assistant-icp-no-published">
          No published ICP yet. Publish one above, then come back here to connect it.
        </div>
      ) : (
        <div className="space-y-2">
          {assistants.map((a) => (
            <div
              key={a.id}
              data-testid={`assistant-icp-row-${a.id}`}
              className="flex flex-wrap items-center gap-2 bg-slate-50 rounded-lg px-3 py-2"
            >
              <span className="text-sm font-medium text-slate-800 min-w-0 truncate flex-1">
                {a.assistant_name || a.id}
                {a.is_primary && <span className="text-[10px] text-slate-400 ml-1.5">(primary)</span>}
              </span>
              <select
                data-testid={`assistant-icp-select-${a.id}`}
                value={currentSelection(a)}
                onChange={(e) => setSelections((prev) => ({ ...prev, [a.id]: e.target.value }))}
                className="h-9 rounded-lg border border-slate-200 px-2 text-sm bg-white"
              >
                <option value="">— no ICP —</option>
                {publishedIcps.map((icp) => (
                  <option key={icp.id} value={icp.id}>{icpLabel(icp)}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => save(a)}
                disabled={savingId === a.id || currentSelection(a) === (a.icp_id || "")}
                data-testid={`assistant-icp-save-${a.id}`}
                className="h-9 px-3 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-50"
              >
                {savingId === a.id ? "…" : "Save"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ICP Editor — create + edit, one component. Buyer Personas section is
// gated behind the ICP actually existing (persona LINKS are version-owned
// and need a real icp_id + version to attach to).
// ---------------------------------------------------------------------------

const EMPTY_FORM = {
  target_type: "organization",
  name: "",
  description: "",
  industry: "",
  geography: "",
  market_segment_name: "",
  market_segment_tags: [],
  company_size_range: "",
  employee_count_range: "",
  annual_revenue_range: "",
  pain_points: [],
  buying_triggers: [],
  goals: [],
  value_proposition: "",
  differentiators: [],
  key_messages: [],
  objections: [], // [{text, response}]
  discovery_questions: [],
  qualification_criteria: [], // [{field, operator, value}]
  sales_strategy: "",
};

function formFromVersion(v) {
  return {
    target_type: v.target_type || "organization",
    name: v.identity_targeting?.name || "",
    description: v.identity_targeting?.description || "",
    industry: v.firmographic_demographic?.industry || "",
    geography: v.firmographic_demographic?.geography || "",
    market_segment_name: v.identity_targeting?.market_segment_name || "",
    market_segment_tags: v.identity_targeting?.market_segment_tags || [],
    company_size_range: v.firmographic_demographic?.company_size_range || "",
    employee_count_range: v.firmographic_demographic?.employee_count_range || "",
    annual_revenue_range: v.firmographic_demographic?.annual_revenue_range || "",
    pain_points: (v.pain_points_motivations?.items || []).map((i) => i.text),
    buying_triggers: (v.buying_triggers?.items || []).map((i) => i.text),
    goals: v.goals?.items || [],
    value_proposition: v.messaging_value_proposition?.value_proposition || "",
    differentiators: v.company_profile?.differentiators || [],
    key_messages: v.messaging_value_proposition?.key_messages || [],
    objections: (v.objections_positioning?.items || []).map((o) => ({ text: o.text, response: o.response || "" })),
    discovery_questions: (v.discovery_questions?.items || []).map((q) => q.question),
    qualification_criteria: (v.qualification_criteria?.criteria || []).map((c) => ({ ...c })),
    sales_strategy: v.sales_strategy?.approach || "",
  };
}

function payloadFromForm(f) {
  return {
    target_type: f.target_type,
    identity_targeting: {
      name: f.name.trim() || null,
      description: f.description.trim() || null,
      market_segment_name: f.market_segment_name.trim() || null,
      market_segment_tags: cleanStrings(f.market_segment_tags),
    },
    firmographic_demographic: {
      industry: f.industry.trim() || null,
      geography: f.geography.trim() || null,
      company_size_range: f.company_size_range.trim() || null,
      employee_count_range: f.employee_count_range.trim() || null,
      annual_revenue_range: f.annual_revenue_range.trim() || null,
    },
    pain_points_motivations: {
      items: cleanStrings(f.pain_points).map((text) => ({ text, visibility: "public" })),
    },
    buying_triggers: {
      items: cleanStrings(f.buying_triggers).map((text) => ({ text, visibility: "public" })),
    },
    goals: { items: cleanStrings(f.goals) },
    messaging_value_proposition: {
      value_proposition: f.value_proposition.trim() || null,
      key_messages: cleanStrings(f.key_messages),
    },
    company_profile: { differentiators: cleanStrings(f.differentiators) },
    objections_positioning: {
      items: (f.objections || [])
        .filter((o) => (o.text || "").trim())
        .map((o) => ({ text: o.text.trim(), response: (o.response || "").trim() || null, visibility: "internal" })),
    },
    discovery_questions: {
      items: cleanStrings(f.discovery_questions).map((question) => ({ question })),
    },
    qualification_criteria: {
      criteria: (f.qualification_criteria || [])
        .filter((c) => (c.field || "").trim() && (c.operator || "").trim() && (c.value || "").trim())
        .map((c) => ({ field: c.field.trim(), operator: c.operator.trim(), value: c.value.trim() })),
    },
    sales_strategy: { approach: f.sales_strategy.trim() || null },
  };
}

function IcpEditor({
  mode, icpId: initialIcpId, version: initialVersion,
  publishedVersionNumber, draftVersionNumber,
  onClose, onSaved,
}) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [icpId, setIcpId] = useState(initialIcpId || null);
  const [version, setVersion] = useState(initialVersion || null);
  const [status, setStatus] = useState("draft");
  const [contentRevision, setContentRevision] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(mode === "edit");
  const [submitting, setSubmitting] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(() => JSON.stringify(EMPTY_FORM));
  const [confirmClose, setConfirmClose] = useState(false);
  // The sibling version's number — updated locally after a mutation so
  // the banner stays correct without re-fetching the whole Library row.
  const [pubVersionNum, setPubVersionNum] = useState(publishedVersionNumber ?? null);
  const [draftVersionNum, setDraftVersionNum] = useState(draftVersionNumber ?? null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const isDraft = status === "draft";
  const exists = Boolean(icpId && version);
  const isDirty = exists && !loadingInitial && JSON.stringify(form) !== savedSnapshot;

  useEffect(() => {
    if (mode !== "edit" || !initialIcpId || !initialVersion) return;
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/icp/${initialIcpId}/versions/${initialVersion}`);
        const v = r.data.version;
        if (!alive) return;
        const f = formFromVersion(v);
        setForm(f);
        setSavedSnapshot(JSON.stringify(f));
        setStatus(v.status);
        setContentRevision(v.content_revision);
      } catch (e) {
        toast.error("Could not load this ICP.");
        onClose();
      } finally {
        if (alive) setLoadingInitial(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialIcpId, initialVersion]);

  const requestClose = () => {
    if (isDirty) { setConfirmClose(true); return; }
    onClose();
  };

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error("ICP name is required.");
    setSubmitting(true);
    try {
      const r = await api.post("/icp", payloadFromForm(form));
      setIcpId(r.data.identity.id);
      setVersion(r.data.version.version);
      setStatus(r.data.version.status);
      setContentRevision(r.data.version.content_revision);
      setDraftVersionNum(r.data.version.version);
      setSavedSnapshot(JSON.stringify(form));
      toast.success("ICP created as a draft. Now add Buyer Personas and publish when ready.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create ICP.");
    } finally {
      setSubmitting(false);
    }
  };

  // Shared by handleSaveDraft and handlePublish so a click on Publish
  // always persists the in-memory form first — never publishes stale
  // server-side content behind the tenant's back.
  const saveCurrentDraft = async () => {
    const r = await api.patch(`/icp/${icpId}/versions/${version}`, {
      ...payloadFromForm(form),
      expected_content_revision: contentRevision,
    });
    setContentRevision(r.data.version.content_revision);
    setSavedSnapshot(JSON.stringify(form));
  };

  const reportSaveError = (e) => {
    const detail = e?.response?.data?.detail;
    if (detail?.reason === "stale_content_revision") {
      setContentRevision(detail.current_content_revision);
      toast.error("Someone else changed this draft. Refreshed to the latest version — please review and save again.");
    } else if (detail?.reason === "not_draft") {
      toast.error("This version is no longer a draft and can't be edited.");
    } else {
      toast.error("Could not save the draft.");
    }
  };

  const handleSaveDraft = async () => {
    if (!form.name.trim()) return toast.error("ICP name is required.");
    setSubmitting(true);
    try {
      await saveCurrentDraft();
      toast.success(`Draft version ${version} saved.`);
      onSaved();
    } catch (e) {
      reportSaveError(e);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublish = async () => {
    if (!form.name.trim()) return toast.error("ICP name is required.");
    setSubmitting(true);
    try {
      try {
        await saveCurrentDraft();
      } catch (e) {
        reportSaveError(e);
        return;
      }
      const r = await api.post(`/icp/${icpId}/versions/${version}/publish`);
      setStatus(r.data.version.status);
      setPubVersionNum(version);
      setDraftVersionNum(null);
      toast.success(`Published! Version ${version} is now live.`);
      onSaved();
    } catch (e) {
      // Reaching here means saveCurrentDraft already succeeded (the save
      // step above returns early on its own failure) — the tenant's
      // edits are safely saved as a draft even though publish itself
      // failed, so every message below says so explicitly rather than
      // leaving the tenant unsure whether their work survived.
      const detail = e?.response?.data?.detail;
      if (detail?.reason === "review_not_approved") {
        toast.error(`Draft version ${version} was saved, but publishing failed: this ICP needs an approved review before it can be published.`);
      } else if (detail?.reason === "publish_lock_held") {
        toast.error(`Draft version ${version} was saved, but publishing failed: a publish is already in progress for this ICP. Try again shortly.`);
      } else if (detail?.reason === "not_draft") {
        toast.error(`Draft version ${version} was saved. This version has already been published — reopen the ICP to see the current state.`);
      } else {
        toast.error(`Draft version ${version} was saved, but publishing failed. Please try again.`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewDraft = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/icp/${icpId}/versions/${version}/new-draft`);
      const v = r.data.version;
      const f = formFromVersion(v);
      setVersion(v.version);
      setStatus(v.status);
      setContentRevision(v.content_revision);
      setForm(f);
      setSavedSnapshot(JSON.stringify(f));
      setDraftVersionNum(v.version);
      if (r.data.reused_existing_draft) {
        toast.success(`Resuming your existing working draft (version ${v.version}).`);
      } else {
        toast.success(`New draft (version ${v.version}) created — content and links carried over from version ${version}.`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create a new draft.");
    } finally {
      setSubmitting(false);
    }
  };

  const bannerText = () => {
    if (!exists) return null;
    if (isDraft) {
      return pubVersionNum != null
        ? `You are editing draft version ${version}. Published version ${pubVersionNum} remains live until this draft is published.`
        : `You are editing draft version ${version}. It is not yet published.`;
    }
    return "This is the live published version and is read-only. Create a working draft to make changes.";
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-3 py-8 overflow-y-auto" data-testid="icp-editor">
      <div className="absolute inset-0 bg-black/40" onClick={requestClose} />
      <div className="relative z-10 w-full max-w-3xl bg-white rounded-2xl border border-slate-200 shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            {exists ? (form.name || "Edit ICP") : "New ICP"}
            {exists && <StatusPill status={status} />}
          </h2>
          <button type="button" onClick={requestClose} className="text-slate-500 hover:text-slate-900" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {loadingInitial ? (
          <div className="grid place-items-center py-20"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
        ) : (
          <div className="p-5 space-y-6">
            {exists && (
              <div
                className={"text-xs rounded-lg px-3 py-2 border " + (isDraft
                  ? "bg-sky-50 border-sky-200 text-sky-800"
                  : "bg-amber-50 border-amber-200 text-amber-800")}
                data-testid="icp-editor-banner"
              >
                {bannerText()}
              </div>
            )}

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-800">Basic Information</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="ICP name *">
                  <input
                    value={form.name}
                    onChange={(e) => set({ name: e.target.value })}
                    disabled={exists && !isDraft}
                    placeholder="e.g., Independent Restaurants"
                    data-testid="icp-field-name"
                    className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm disabled:bg-slate-50"
                  />
                </Field>
                <Field label="ICP type">
                  <select
                    value={form.target_type}
                    onChange={(e) => set({ target_type: e.target.value })}
                    disabled={exists && !isDraft}
                    data-testid="icp-field-target-type"
                    className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm disabled:bg-slate-50"
                  >
                    {TARGET_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </Field>
                <Field label="Industry or market">
                  <input
                    value={form.industry}
                    onChange={(e) => set({ industry: e.target.value })}
                    disabled={exists && !isDraft}
                    placeholder="e.g., Restaurants"
                    data-testid="icp-field-industry"
                    className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm disabled:bg-slate-50"
                  />
                </Field>
                <Field label="Geography">
                  <input
                    value={form.geography}
                    onChange={(e) => set({ geography: e.target.value })}
                    disabled={exists && !isDraft}
                    placeholder="e.g., United States"
                    data-testid="icp-field-geography"
                    className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm disabled:bg-slate-50"
                  />
                </Field>
              </div>
              <Field label="Short description">
                <textarea
                  value={form.description}
                  onChange={(e) => set({ description: e.target.value })}
                  disabled={exists && !isDraft}
                  rows={2}
                  data-testid="icp-field-description"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50"
                />
              </Field>
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-800">Target Customer</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Market segment">
                  <input
                    value={form.market_segment_name}
                    onChange={(e) => set({ market_segment_name: e.target.value })}
                    disabled={exists && !isDraft}
                    data-testid="icp-field-market-segment"
                    className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm disabled:bg-slate-50"
                  />
                </Field>
                <Field label="Company size / scale">
                  <input
                    value={form.company_size_range}
                    onChange={(e) => set({ company_size_range: e.target.value })}
                    disabled={exists && !isDraft}
                    placeholder="e.g., 10-50 employees"
                    data-testid="icp-field-company-size"
                    className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm disabled:bg-slate-50"
                  />
                </Field>
                <Field label="Employee count">
                  <input
                    value={form.employee_count_range}
                    onChange={(e) => set({ employee_count_range: e.target.value })}
                    disabled={exists && !isDraft}
                    className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm disabled:bg-slate-50"
                  />
                </Field>
                <Field label="Annual revenue range">
                  <input
                    value={form.annual_revenue_range}
                    onChange={(e) => set({ annual_revenue_range: e.target.value })}
                    disabled={exists && !isDraft}
                    className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm disabled:bg-slate-50"
                  />
                </Field>
              </div>
              <StringListField
                label="Company characteristics"
                hint="One per line."
                value={form.market_segment_tags}
                onChange={(v) => set({ market_segment_tags: v })}
              />
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-800">Buyer Personas</h3>
              {!exists ? (
                <div className="text-xs text-slate-500 bg-slate-50 rounded-lg px-3 py-2">
                  Save this ICP first to add Buyer Personas.
                </div>
              ) : (
                <PersonaLinksEditor icpId={icpId} version={version} isDraft={isDraft} />
              )}
            </section>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-800">Commercial Context</h3>
              <fieldset disabled={exists && !isDraft} className="space-y-3">
                <StringListField label="Pain points" hint="One per line." value={form.pain_points} onChange={(v) => set({ pain_points: v })} />
                <StringListField label="Buying triggers" hint="One per line." value={form.buying_triggers} onChange={(v) => set({ buying_triggers: v })} />
                <StringListField label="Goals / desired outcomes" hint="One per line." value={form.goals} onChange={(v) => set({ goals: v })} />
                <Field label="Value proposition">
                  <textarea
                    value={form.value_proposition}
                    onChange={(e) => set({ value_proposition: e.target.value })}
                    rows={2}
                    data-testid="icp-field-value-prop"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50"
                  />
                </Field>
                <StringListField label="Differentiators" hint="One per line." value={form.differentiators} onChange={(v) => set({ differentiators: v })} />
                <StringListField label="Key messaging" hint="One per line." value={form.key_messages} onChange={(v) => set({ key_messages: v })} />

                <ObjectionsEditor value={form.objections} onChange={(v) => set({ objections: v })} />

                <StringListField label="Discovery questions" hint="One per line." value={form.discovery_questions} onChange={(v) => set({ discovery_questions: v })} />

                <QualificationCriteriaEditor value={form.qualification_criteria} onChange={(v) => set({ qualification_criteria: v })} />

                <Field label="Sales strategy / recommended approach">
                  <textarea
                    value={form.sales_strategy}
                    onChange={(e) => set({ sales_strategy: e.target.value })}
                    rows={2}
                    data-testid="icp-field-sales-strategy"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50"
                  />
                </Field>
              </fieldset>
            </section>
          </div>
        )}

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 sticky bottom-0 bg-white rounded-b-2xl">
          <button type="button" onClick={requestClose} className="px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50" data-testid="icp-editor-close">
            Close
          </button>
          {!exists ? (
            <button
              type="button"
              onClick={handleCreate}
              disabled={submitting}
              data-testid="icp-editor-create"
              className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Create ICP
            </button>
          ) : isDraft ? (
            <>
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={submitting}
                data-testid="icp-editor-save-draft"
                className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                Save draft
              </button>
              <button
                type="button"
                onClick={handlePublish}
                disabled={submitting}
                data-testid="icp-editor-publish"
                className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-2"
              >
                <Rocket className="w-4 h-4" /> Save & Publish
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleNewDraft}
              disabled={submitting}
              data-testid="icp-editor-new-draft"
              className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-60"
            >
              Create new draft to edit
            </button>
          )}
        </div>
      </div>

      <ConfirmModal
        open={confirmClose}
        title="Discard unsaved changes?"
        message="You have changes that haven't been saved. Closing now will discard them."
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        tone="danger"
        onCancel={() => setConfirmClose(false)}
        onConfirm={() => { setConfirmClose(false); onClose(); }}
        testIdPrefix="icp-editor-unsaved"
      />
    </div>
  );
}

function ObjectionsEditor({ value, onChange }) {
  const rows = value || [];
  const update = (i, patch) => {
    const next = rows.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { text: "", response: "" }]);
  return (
    <Field label="Common objections">
      <div className="space-y-2">
        {rows.map((o, i) => (
          <div key={i} className="flex gap-2 items-start" data-testid={`icp-objection-row-${i}`}>
            <input
              value={o.text}
              onChange={(e) => update(i, { text: e.target.value })}
              placeholder="Objection"
              className="flex-1 h-9 rounded-lg border border-slate-300 px-2 text-sm"
            />
            <input
              value={o.response}
              onChange={(e) => update(i, { response: e.target.value })}
              placeholder="How to respond"
              className="flex-1 h-9 rounded-lg border border-slate-300 px-2 text-sm"
            />
            <button type="button" onClick={() => remove(i)} className="text-slate-400 hover:text-red-600 p-2" aria-label="Remove">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button type="button" onClick={add} data-testid="icp-objection-add" className="text-xs text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Add objection
        </button>
      </div>
    </Field>
  );
}

function QualificationCriteriaEditor({ value, onChange }) {
  const rows = value || [];
  const update = (i, patch) => {
    const next = rows.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { field: "", operator: "", value: "" }]);
  return (
    <Field label="Qualification guidance" hint="e.g., Field: company_size, Operator: gte, Value: 50 employees.">
      <div className="space-y-2">
        {rows.map((c, i) => (
          <div key={i} className="flex gap-2 items-start" data-testid={`icp-qualification-row-${i}`}>
            <input value={c.field} onChange={(e) => update(i, { field: e.target.value })} placeholder="Field" className="flex-1 h-9 rounded-lg border border-slate-300 px-2 text-sm" />
            <input value={c.operator} onChange={(e) => update(i, { operator: e.target.value })} placeholder="Operator" className="w-24 h-9 rounded-lg border border-slate-300 px-2 text-sm" />
            <input value={c.value} onChange={(e) => update(i, { value: e.target.value })} placeholder="Value" className="flex-1 h-9 rounded-lg border border-slate-300 px-2 text-sm" />
            <button type="button" onClick={() => remove(i)} className="text-slate-400 hover:text-red-600 p-2" aria-label="Remove">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button type="button" onClick={add} data-testid="icp-qualification-add" className="text-xs text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Add criterion
        </button>
      </div>
    </Field>
  );
}

// ---------------------------------------------------------------------------
// Buyer Persona linking — tenant persona library (reused unchanged) +
// version-owned persona links (Package 1B.4).
// ---------------------------------------------------------------------------

function PersonaLinksEditor({ icpId, version, isDraft }) {
  const [links, setLinks] = useState([]);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPersonaId, setSelectedPersonaId] = useState("");
  const [buyingRole, setBuyingRole] = useState("");
  const [busy, setBusy] = useState(false);
  const [showNewPersona, setShowNewPersona] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [l, p] = await Promise.all([
        api.get(`/icp/${icpId}/versions/${version}/personas`),
        api.get("/icp/personas"),
      ]);
      setLinks(l.data.persona_links || []);
      setLibrary(p.data.personas || []);
    } catch (_e) {
      toast.error("Could not load Buyer Personas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [icpId, version]); // eslint-disable-line react-hooks/exhaustive-deps

  const linkedIds = new Set(links.map((l) => l.persona_id));
  const available = library.filter((p) => !linkedIds.has(p.id));
  const personaById = (id) => library.find((p) => p.id === id);

  const addLink = async () => {
    if (!selectedPersonaId) return;
    setBusy(true);
    try {
      await api.post(`/icp/${icpId}/versions/${version}/personas`, {
        persona_id: selectedPersonaId,
        buying_role: buyingRole.trim() || null,
      });
      toast.success("Persona added to this ICP.");
      setSelectedPersonaId(""); setBuyingRole("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add this Persona.");
    } finally {
      setBusy(false);
    }
  };

  const removeLink = async (linkId) => {
    setBusy(true);
    try {
      await api.delete(`/icp/${icpId}/versions/${version}/personas/${linkId}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove this Persona.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="py-4 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-3">
      {links.length === 0 ? (
        <div className="text-xs text-slate-500" data-testid="icp-personas-empty">No Buyer Personas linked yet.</div>
      ) : (
        <ul className="space-y-1.5" data-testid="icp-personas-list">
          {links.map((l) => {
            const p = personaById(l.persona_id);
            return (
              <li key={l.id} data-testid={`icp-persona-row-${l.id}`} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-sm">
                <span className="min-w-0 truncate">
                  <span className="font-medium">{p?.name || l.persona_id}</span>
                  {l.buying_role && <span className="text-slate-400 ml-2 text-xs">({l.buying_role})</span>}
                </span>
                {isDraft && (
                  <button type="button" onClick={() => removeLink(l.id)} disabled={busy} className="text-slate-400 hover:text-red-600 p-1" aria-label="Remove persona" data-testid={`icp-persona-remove-${l.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {isDraft && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={selectedPersonaId}
              onChange={(e) => setSelectedPersonaId(e.target.value)}
              data-testid="icp-persona-select"
              className="h-9 rounded-lg border border-slate-200 px-2 text-sm bg-white"
            >
              <option value="">— choose an existing persona —</option>
              {available.map((p) => <option key={p.id} value={p.id}>{p.name}{p.role ? ` (${p.role})` : ""}</option>)}
            </select>
            <input
              value={buyingRole}
              onChange={(e) => setBuyingRole(e.target.value)}
              placeholder="Decision-maker role (e.g., Economic Buyer)"
              data-testid="icp-persona-buying-role"
              className="h-9 rounded-lg border border-slate-200 px-2 text-sm flex-1 min-w-[200px]"
            />
            <button
              type="button"
              onClick={addLink}
              disabled={!selectedPersonaId || busy}
              data-testid="icp-persona-add-existing"
              className="h-9 px-3 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-50"
            >
              Add
            </button>
          </div>
          <button
            type="button"
            onClick={() => setShowNewPersona((s) => !s)}
            data-testid="icp-persona-toggle-new"
            className="text-xs text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" /> {showNewPersona ? "Cancel" : "Create a new Persona"}
          </button>
          {showNewPersona && (
            <NewPersonaForm
              onCreated={async (personaId) => {
                setShowNewPersona(false);
                setSelectedPersonaId("");
                setBusy(true);
                try {
                  await api.post(`/icp/${icpId}/versions/${version}/personas`, { persona_id: personaId, buying_role: buyingRole.trim() || null });
                  toast.success("Persona created and added to this ICP.");
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Persona created, but could not be linked.");
                } finally {
                  setBusy(false);
                }
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function NewPersonaForm({ onCreated }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [motivations, setMotivations] = useState([]);
  const [objections, setObjections] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!name.trim()) return toast.error("Persona name is required.");
    setSubmitting(true);
    try {
      const r = await api.post("/icp/personas", {
        name: name.trim(),
        role: role.trim() || null,
        motivations: cleanStrings(motivations),
        objections: cleanStrings(objections),
      });
      onCreated(r.data.persona.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create Persona.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50" data-testid="icp-new-persona-form">
      <div className="grid sm:grid-cols-2 gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Persona name *" data-testid="icp-new-persona-name" className="h-9 rounded-lg border border-slate-300 px-2 text-sm bg-white" />
        <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role or title" data-testid="icp-new-persona-role" className="h-9 rounded-lg border border-slate-300 px-2 text-sm bg-white" />
      </div>
      <StringListField label="Goals / motivations" hint="One per line." value={motivations} onChange={setMotivations} />
      <StringListField label="Objections" hint="One per line." value={objections} onChange={setObjections} />
      <button
        type="button"
        onClick={submit}
        disabled={submitting}
        data-testid="icp-new-persona-submit"
        className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
      >
        {submitting ? "Creating…" : "Create & add Persona"}
      </button>
    </div>
  );
}
